'use client';

import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type LenisScrollEvent = 'scroll' | 'virtual-scroll';
type LenisEventCallback = (event: unknown) => void;

type LenisInstance = {
  raf: (time: number) => void;
  destroy: () => void;
  scrollTo: (
    target: string | HTMLElement,
    options?: {
      offset?: number;
    },
  ) => void;
  on: (event: LenisScrollEvent, callback: LenisEventCallback) => void;
  off: (event: LenisScrollEvent, callback: LenisEventCallback) => void;
};

type LenisConstructor = new (options: {
  wrapper: HTMLDivElement;
  content: HTMLDivElement;
  duration: number;
  easing: (time: number) => number;
  wheelMultiplier: number;
  touchMultiplier: number;
  smoothWheel: boolean;
  syncTouch: boolean;
}) => LenisInstance;

type Props = PropsWithChildren<{
  className?: string;
  contentClassName?: string;
}>;

const LenisContext = createContext<LenisInstance | null>(null);

export function useLenis(): LenisInstance | null {
  return useContext(LenisContext);
}

export function SmoothScrollProvider({
  children,
  className,
  contentClassName,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [lenis, setLenis] = useState<LenisInstance | null>(null);

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    let cancelled = false;
    let rafId = 0;
    let instance: LenisInstance | null = null;

    const setup = async () => {
      const lenisModule = await import('lenis');
      if (cancelled || !wrapperRef.current || !contentRef.current) return;

      const Lenis = lenisModule.default as LenisConstructor;
      instance = new Lenis({
        wrapper: wrapperRef.current,
        content: contentRef.current,
        duration: 1.15,
        easing: (t) => 1 - Math.pow(1 - t, 4),
        wheelMultiplier: 0.92,
        touchMultiplier: 1,
        smoothWheel: true,
        syncTouch: false,
      });

      setLenis(instance);

      if (process.env.NODE_ENV !== 'production') {
        (window as Window & { __LENIS__?: LenisInstance }).__LENIS__ = instance;
      }

      const raf = (time: number) => {
        instance?.raf(time);
        rafId = requestAnimationFrame(raf);
      };

      rafId = requestAnimationFrame(raf);
    };

    setup();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      instance?.destroy();
      setLenis(null);
      if (process.env.NODE_ENV !== 'production') {
        delete (window as Window & { __LENIS__?: LenisInstance }).__LENIS__;
      }
    };
  }, []);

  return (
    <LenisContext.Provider value={lenis}>
      <div ref={wrapperRef} data-lenis-wrapper className={className}>
        <div ref={contentRef} data-lenis-content className={contentClassName}>
          {children}
        </div>
      </div>
    </LenisContext.Provider>
  );
}
